import { NavLink, Outlet } from 'react-router-dom';
import {
  ClipboardListIcon,
  PhoneIcon,
  ScrollTextIcon,
  SettingsIcon,
  ShieldIcon,
  UsersIcon
} from 'lucide-react';

import { useListPhoneApprovals } from '@cdf/api-client-react';
import { cn } from '@/lib/utils';

const sections = [
  { title: 'Overview', to: '/admin', end: true, icon: ShieldIcon },
  { title: 'Users', to: '/admin/users', icon: UsersIcon },
  { title: 'Audit log', to: '/admin/audit-log', icon: ScrollTextIcon },
  { title: 'Settings', to: '/settings', end: true, icon: SettingsIcon },
  { title: 'Attendance register', to: '/settings/attendance', icon: ClipboardListIcon },
  { title: 'Approved numbers', to: '/approved-numbers', icon: PhoneIcon, showPending: true }
] as const;

function PendingCountBadge() {
  const { data } = useListPhoneApprovals({ status: 'pending' });
  const count = data?.total ?? 0;
  if (count <= 0) return null;
  return (
    <span className="ml-auto rounded-full bg-destructive/15 px-1.5 py-0.5 text-[10px] font-bold tabular-nums text-destructive">
      {count}
    </span>
  );
}

/**
 * Admin area chrome — secondary nav for admin tools. The main sidebar only
 * links here as a single "Admin" entry.
 */
export function AdminLayout() {
  return (
    <div className="flex flex-col gap-6">
      <nav
        aria-label="Administration"
        className="border-border/80 bg-muted/30 flex flex-wrap gap-1 rounded-xl border p-1"
      >
        {sections.map((section) => {
          const Icon = section.icon;
          return (
            <NavLink
              key={section.to}
              to={section.to}
              end={'end' in section ? section.end : false}
              className={({ isActive }) =>
                cn(
                  'inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:bg-background/60 hover:text-foreground'
                )
              }
            >
              <Icon className="size-4 shrink-0" aria-hidden />
              <span>{section.title}</span>
              {'showPending' in section && section.showPending ? <PendingCountBadge /> : null}
            </NavLink>
          );
        })}
      </nav>
      <Outlet />
    </div>
  );
}
